/**
 * Messaging Automation Settings Context
 *
 * Productized control over messaging automation behavior.
 * Beta default: Suggestive Mode (no auto-send without explicit user action).
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

export type MessagingMode = "suggestive" | "automated";
export type FollowUpSendMode = "suggestive" | "automated";

export interface SourceAutomationOverride {
  source: string;
  messagingMode: MessagingMode;
  followUpSendMode: FollowUpSendMode;
}

export interface MessagingAutomationSettings {
  /** Global messaging mode — controls outbound conversation automation */
  messagingMode: MessagingMode;
  /** Global follow-up send mode — controls disposition follow-up sends */
  followUpSendMode: FollowUpSendMode;
  /** When true, all outbound messages require manual review before send */
  requireReviewBeforeSend: boolean;
  /** Per-source overrides (source name → settings) */
  sourceOverrides: Record<string, SourceAutomationOverride>;
}

const KNOWN_SOURCES = ["Angi", "HomeAdvisor", "Google Ads", "Facebook Ads", "Website", "Referral", "Home Show"];

/** Beta-safe defaults: everything suggestive, review required */
const BETA_DEFAULTS: MessagingAutomationSettings = {
  messagingMode: "suggestive",
  followUpSendMode: "suggestive",
  requireReviewBeforeSend: true,
  sourceOverrides: Object.fromEntries(
    KNOWN_SOURCES.map(s => [s, { source: s, messagingMode: "suggestive" as MessagingMode, followUpSendMode: "suggestive" as FollowUpSendMode }])
  ),
};

/** Roles allowed to modify messaging automation settings */
export const MESSAGING_SETTINGS_WRITE_ROLES = ["super_admin", "owner", "admin", "it_staff"] as const;
export const MESSAGING_SETTINGS_VIEW_ROLES = ["super_admin", "owner", "admin", "it_staff", "sales_manager"] as const;

export function canEditMessagingSettings(role: string): boolean {
  return (MESSAGING_SETTINGS_WRITE_ROLES as readonly string[]).includes(role);
}

export function canViewMessagingSettings(role: string): boolean {
  return (MESSAGING_SETTINGS_VIEW_ROLES as readonly string[]).includes(role);
}

interface MessagingAutomationContextValue {
  settings: MessagingAutomationSettings;
  updateSettings: (patch: Partial<MessagingAutomationSettings>) => void;
  updateSourceOverride: (source: string, patch: Partial<SourceAutomationOverride>) => void;
  /** Resolved check: should a message for this source be auto-sent? */
  shouldAutoSend: (source?: string) => boolean;
  /** Resolved check: should a follow-up for this source be auto-sent? */
  shouldAutoSendFollowUp: (source?: string) => boolean;
  /** List of known sources */
  knownSources: string[];
}

const MessagingAutomationContext = createContext<MessagingAutomationContextValue | null>(null);

export function MessagingAutomationProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<MessagingAutomationSettings>(BETA_DEFAULTS);

  const updateSettings = useCallback((patch: Partial<MessagingAutomationSettings>) => {
    setSettings(prev => ({ ...prev, ...patch }));
  }, []);

  const updateSourceOverride = useCallback((source: string, patch: Partial<SourceAutomationOverride>) => {
    setSettings(prev => ({
      ...prev,
      sourceOverrides: {
        ...prev.sourceOverrides,
        [source]: { ...prev.sourceOverrides[source], source, ...patch },
      },
    }));
  }, []);

  const shouldAutoSend = useCallback((source?: string): boolean => {
    if (settings.requireReviewBeforeSend) return false;
    if (source && settings.sourceOverrides[source]) {
      return settings.sourceOverrides[source].messagingMode === "automated";
    }
    return settings.messagingMode === "automated";
  }, [settings]);

  const shouldAutoSendFollowUp = useCallback((source?: string): boolean => {
    if (settings.requireReviewBeforeSend) return false;
    if (source && settings.sourceOverrides[source]) {
      return settings.sourceOverrides[source].followUpSendMode === "automated";
    }
    return settings.followUpSendMode === "automated";
  }, [settings]);

  const value = useMemo(() => ({
    settings,
    updateSettings,
    updateSourceOverride,
    shouldAutoSend,
    shouldAutoSendFollowUp,
    knownSources: KNOWN_SOURCES,
  }), [settings, updateSettings, updateSourceOverride, shouldAutoSend, shouldAutoSendFollowUp]);

  return (
    <MessagingAutomationContext.Provider value={value}>
      {children}
    </MessagingAutomationContext.Provider>
  );
}

export function useMessagingAutomation() {
  const ctx = useContext(MessagingAutomationContext);
  if (!ctx) throw new Error("useMessagingAutomation must be used within MessagingAutomationProvider");
  return ctx;
}
